// campaignLauncher.js
// The NEW-GAME campaign-start pipeline, extracted from NewGame's handleSubmit so
// it is testable in isolation. Given a campaign spec (from a story template or
// the New Game form) it: merges milestone location names into customNames,
// generates the world map, spawns milestone entities, pre-generates every town
// map (quest buildings + NPCs baked in), picks map-valid side quests, and
// assembles the game_settings snapshot.
//
// In-save continuation ("Continue your legend") does NOT use this pipeline; it
// spawns additively into the existing world instead (see campaignChain.js).
//
// Pure-ish: deterministic given a seed (map/town/NPC/side-quest generation are all
// seeded); the only non-deterministic pieces are the fallback random seed and the
// minted gameSessionId, both overridable via options for tests.
//
// See docs/QUEST_CHAINING_PLAN.md for the design.

import { generateMapData } from '../utils/mapGenerator';
import { generateTownMap } from '../utils/townMapGenerator';
import { getTownWaterContext, getTownRoadEdges } from '../utils/townWater';
import { selectTownSideQuests, planSideQuests } from './questEngine';
import { populateTown } from '../utils/npcGenerator';
import { spawnWorldMapEntities, injectQuestBuildings, findMissingMilestoneLocations } from './milestoneSpawner';
import { getMilestoneLocationNames, getMilestoneNpcsForTown } from './milestoneEngine';
import { isPremium, isThemePremium, isTemplatePremium } from './entitlements';
import { resolveWaterTownAccess, waterTownWorldGenOptions, stampWaterTowns } from './waterTowns';
import { createLogger } from '../utils/logger';
import { normalizeWorldSeed } from '../utils/worldSeed';

const logger = createLogger('campaign-launcher');

// Merge milestone location names into customNames so the map generator places them.
// (Moved verbatim from NewGame.js.)
export const mergeLocationNames = (customNames, milestones) => {
    const milestoneNames = getMilestoneLocationNames(milestones);
    const towns = [...(customNames?.towns || [])];
    const mountains = [...(customNames?.mountains || [])];
    // Town entries may be plain strings or { name, size } objects (size-tagged locations).
    const nameOf = (entry) => (typeof entry === 'string' ? entry : entry?.name || '');

    // A milestone name already authored in EITHER list keeps the template's placement.
    // getMilestoneLocationNames only guesses town vs mountain from the milestone type
    // (item -> town, location/combat -> mountain), so checking one list alone duplicated
    // names across both, e.g. the Grey Moors range also appearing as a town.
    const isAuthored = (name) => [...towns, ...mountains]
        .some(entry => nameOf(entry).toLowerCase() === name.toLowerCase());
    for (const name of milestoneNames.towns) {
        if (!isAuthored(name)) towns.push(name);
    }
    for (const name of milestoneNames.mountains) {
        if (!isAuthored(name)) mountains.push(name);
    }

    return { towns, mountains };
};

// Resolve milestone location names to map coordinates by matching town and mountain
// names. (Moved verbatim from NewGame.js.)
export const resolveMilestoneCoords = (milestones, mapData) => {
    if (!milestones || !mapData) return milestones;

    // Build a lookup of named locations -> coordinates from the map
    const locationLookup = {};
    for (let y = 0; y < mapData.length; y++) {
        for (let x = 0; x < mapData[y].length; x++) {
            const tile = mapData[y][x];
            if (tile.poi === 'town' && tile.townName) {
                locationLookup[tile.townName.toLowerCase()] = { x, y };
            }
            // For mountains, store the first tile found for each range name
            if (tile.poi === 'mountain' && tile.mountainName) {
                const key = tile.mountainName.toLowerCase();
                if (!locationLookup[key]) {
                    locationLookup[key] = { x, y };
                }
            }
        }
    }

    return milestones
        .filter(m => m.text && m.text.trim())
        .map(m => {
            const resolved = { ...m };
            if (m.location) {
                const coords = locationLookup[m.location.toLowerCase()];
                if (coords) {
                    resolved.mapX = coords.x;
                    resolved.mapY = coords.y;
                }
            }
            return resolved;
        });
};

// New game session ids everywhere use this format (NewGame, useGameSession fallback).
export const mintGameSessionId = () =>
    `game-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// Build a launch spec from a story template (mirrors NewGame's applyTemplate +
// handleSubmit derivations, so chaining launches the exact campaign New Game would).
export const specFromTemplate = (template) => ({
    templateId: template.id,
    templateName: template.subtitle ? `${template.name} — ${template.subtitle}` : template.name,
    shortDescription: template.settings.shortDescription,
    grimnessLevel: template.settings.grimnessLevel,
    darknessLevel: template.settings.darknessLevel,
    magicLevel: template.settings.magicLevel,
    technologyLevel: template.settings.technologyLevel,
    responseVerbosity: template.settings.responseVerbosity,
    campaignGoal: template.settings.campaignGoal || '',
    milestones: template.settings.milestones || [],
    customNames: template.customNames || { towns: [], mountains: [] },
    worldTheme: template.settings.theme || 'grassland',
    tier: template.tier || 1,
    levelRange: template.levelRange || null,
    premium: isTemplatePremium(template),
});

/**
 * Run the full campaign-start pipeline.
 *
 * @param {object} spec - campaign spec (see specFromTemplate; New Game builds one
 *   from its form state): { templateId?, templateName, shortDescription,
 *   grimnessLevel, darknessLevel, magicLevel, technologyLevel, responseVerbosity,
 *   campaignGoal, milestones, customNames, worldTheme, tier, levelRange, premium }
 * @param {object} [options]
 * @param {number|string} [options.seed] - world seed (random when omitted)
 * @param {Array} [options.mapData] - pre-generated map (New Game's manual preview)
 * @param {string} [options.gameSessionId] - session id override (tests)
 * @returns {{ settings, mapData, townMapsCache, worldSeed, gameSessionId }}
 */
export const launchCampaign = (spec, options = {}) => {
    const {
        seed = null,
        mapData: providedMap = null,
        gameSessionId = null,
    } = options;

    // Premium backstop: every path that can start a campaign must hold this gate,
    // not just the New Game screen (which shows a friendlier message first).
    if (!isPremium() && (spec.premium === true || isThemePremium(spec.worldTheme))) {
        throw new Error('This is a Premium adventure. Premium unlock is coming soon — pick a free adventure to begin.');
    }

    const milestones = spec.milestones || [];
    const worldTheme = spec.worldTheme || 'grassland';
    const customNames = spec.customNames || { towns: [], mountains: [] };

    // Typed seeds arrive as strings; normalise so they are not coerced to 42 by the
    // generators (0 is a valid seed, hence ?? rather than ||).
    const seedToUse = normalizeWorldSeed(seed) ?? Math.floor(Math.random() * 1000000);

    // Water towns (#65 Phase 3): the entitlement is checked exactly ONCE, here at New
    // Game. When the tier allows, the world-gen shims run (river to the sea + estuary
    // town) and the chosen settlements' world tiles are stamped `waterTown` below; the
    // save then carries the decision forever (lapsed subscribers keep their water
    // towns). Free/guest tiers get empty options and no stamps: byte-identical worlds.
    const waterAccess = resolveWaterTownAccess();

    // Guard on PROVIDED maps (New Game's optional preview): the preview may have been
    // built under DIFFERENT customNames/theme (previewed before picking the template,
    // or under another template), in which case the milestone towns simply do not
    // exist on it and every quest building/POI/NPC anchored to them would silently
    // never spawn (playtest 2026-07-07: frozen-frontier-t1 started over a stale
    // pre-template preview had no Hearthmere, so its trading post never existed).
    // A map that cannot host the campaign is discarded and regenerated from the same
    // seed with the campaign's own names; a compatible preview is kept byte-identical.
    // Mirrors campaignChain's isTemplateCompatibleWithWorld on the continuation path.
    let mapToUse = providedMap;
    if (mapToUse && milestones.length > 0) {
        const missing = findMissingMilestoneLocations(mapToUse, milestones);
        if (missing.length > 0) {
            logger.warn(`Provided preview map is missing milestone locations (${missing.join(', ')}); regenerating the world for this campaign.`);
            mapToUse = null;
        }
    }
    const mapData = mapToUse
        || generateMapData(10, 10, seedToUse, mergeLocationNames(customNames, milestones), worldTheme, waterTownWorldGenOptions(waterAccess));
    stampWaterTowns(mapData, seedToUse, waterAccess);

    // Spawn milestone entities onto the world map before resolving coords
    const spawnResult = spawnWorldMapEntities(mapData, milestones);

    // Pre-generate all town maps so saves are never affected by generator changes
    const townMapsCache = {};
    for (let y = 0; y < mapData.length; y++) {
        for (let x = 0; x < mapData[y].length; x++) {
            const tile = mapData[y][x];
            if (tile.poi === 'town' && tile.townName) {
                const townSize = tile.townSize || tile.poiType || 'village';
                const townSeed = parseInt(seedToUse) + (x * 1000) + (y * 10000);
                // getTownWaterContext carries the tile's waterTown stamp through as
                // water.archetype (canal/riverfork town layouts, #65); unstamped towns
                // get exactly the old analyzeTownWater context.
                const townMapData = generateTownMap(townSize, tile.townName, getTownRoadEdges(mapData, x, y), townSeed, tile.hasRiver, tile.riverDirection, worldTheme, getTownWaterContext(mapData, x, y));

                // Inject quest buildings if needed
                if (spawnResult.requiredBuildings?.[tile.townName]) {
                    injectQuestBuildings(townMapData, spawnResult.requiredBuildings[tile.townName]);
                }

                // Populate town with NPCs (canonical milestone NPCs replace procedural staff)
                const npcs = populateTown(townMapData, townSeed, getMilestoneNpcsForTown(milestones, tile.townName));
                townMapData.npcs = npcs;

                townMapsCache[tile.townName] = townMapData;
                logger.debug(`Pre-generated town map: ${tile.townName} (${townSize})`);
            }
        }
    }

    const campaignTier = spec.tier || 1;
    const campaignLevelRange = spec.levelRange || (campaignTier === 1 ? [1, 2] : [3, 5]);

    // Pick side quests that can be both STARTED and COMPLETED on this map: only quests
    // whose giver building + objective site + turn-in building all exist. Seeded off the
    // world seed so a given world reproducibly offers the same side quests.
    const flatTiles = [].concat(...mapData);
    const availableSites = {
        cave: flatTiles.some((t) => t.poi === 'cave_entrance'),
        ruins: flatTiles.some((t) => t.poi === 'ruins'),
        // Open site types: gather quests hint these as item sources (questEngine
        // isQuestEligible checks m.sites against this map), so include them here
        // exactly as deriveSideQuestAvailability does at load.
        forest: flatTiles.some((t) => t.poi === 'forest'),
        hills: flatTiles.some((t) => t.poi === 'hills'),
        mountain: flatTiles.some((t) => t.poi === 'mountain'),
    };
    const availableBuildings = new Set();
    Object.values(townMapsCache).forEach((tm) => {
        (tm.mapData || []).forEach((row) => row.forEach((t) => {
            if (t.type === 'building' && t.buildingType) availableBuildings.add(t.buildingType);
        }));
    });
    let sqSeed = parseInt(seedToUse) || 1;
    const sqRng = () => { sqSeed = (sqSeed * 9301 + 49297) % 233280; return sqSeed / 233280; };
    // A few side quests per settlement by size (planSideQuests: city 3, town 2, village
    // and hamlet 1, capped at 8), filled town by town from quests each town's buildings
    // can give, with a level-1 quest first in the starting town and each city. Seeded, so
    // a world is reproducible but worlds differ; per-town pacing (getOfferAt) keeps a
    // town's quests from all showing at once.
    const plan = planSideQuests(flatTiles.filter((t) => t.poi === 'town'));
    const townBuildings = (name) => {
        const set = new Set();
        (townMapsCache[name]?.mapData || []).forEach((row) => row.forEach((t) => { if (t.type === 'building' && t.buildingType) set.add(t.buildingType); }));
        return [...set];
    };
    const selectedSideQuests = selectTownSideQuests(
        { sites: availableSites, buildings: [...availableBuildings], theme: worldTheme, darkness: spec.darknessLevel },
        plan.order.map((t) => ({ ...t, buildings: townBuildings(t.town) })),
        sqRng, townMapsCache);

    // Derive campaignGoal from the final milestone if not explicitly set
    const derivedGoal = spec.campaignGoal || (milestones.length > 0
        ? milestones[milestones.length - 1].text
        : '');

    const settings = {
        shortDescription: spec.shortDescription,
        grimnessLevel: spec.grimnessLevel,
        darknessLevel: spec.darknessLevel,
        magicLevel: spec.magicLevel,
        technologyLevel: spec.technologyLevel,
        responseVerbosity: spec.responseVerbosity,
        campaignGoal: derivedGoal,
        milestones: resolveMilestoneCoords(milestones, mapData),
        worldSeed: seedToUse,
        // Biome theme + map-format version travel with the save (the map itself is a bare
        // 2D array, so version/theme live on game_settings — see WORLD_BIOME_PLAN / CLAUDE.md).
        theme: worldTheme,
        mapVersion: 2,
        templateName: spec.templateName,
        tier: campaignTier,
        levelRange: campaignLevelRange,
        requiredBuildings: spawnResult.requiredBuildings,
        enemySpawns: spawnResult.enemySpawns,
        itemSpawns: spawnResult.itemSpawns,
        sideQuests: selectedSideQuests
    };
    // Additive: template id enables clean chain records (completedCampaigns) later.
    if (spec.templateId) settings.templateId = spec.templateId;

    return {
        settings,
        mapData,
        townMapsCache,
        worldSeed: seedToUse,
        gameSessionId: gameSessionId || mintGameSessionId(),
    };
};
